import { MentionExtensionAttributes } from 'remirror/extensions';
import { FloatingWrapper, useMention } from '@remirror/react';
import { memo, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserRole, UserStatus, useUsersQuery } from '../../types/graphqlTypes';
import { SelectItemContent } from '../selects/SelectItem';
import { UserIcon } from '@heroicons/react/24/outline';

const MentionSuggestions_: React.FC = () => {
  const { organisationId = '', projectId = '' } = useParams();
  const usersQuery = useUsersQuery({ variables: { organisationId } });
  const [users, setUsers] = useState<MentionExtensionAttributes[]>([]);
  const { state, getMenuProps, getItemProps, indexIsSelected } = useMention({
    items: users,
  });
  const organisationUsers = useMemo(() => {
    return (usersQuery.data?.usersByOrganisationId || [])
      .filter((user) => {
        if (user.status !== UserStatus.Active) return false;
        if (user.role === UserRole.ApiKey) return false;
        if (user.role === UserRole.Guest && !user.allowedProjectIds.includes(projectId)) return false;
        return true;
      })
      .map((user) => {
        return { id: user.id, label: user.name };
      });
  }, [usersQuery.data?.usersByOrganisationId, projectId]);

  useEffect(() => {
    if (!state) {
      return;
    }

    const searchTerm = state.query.full.toLowerCase();
    const filteredUsers = organisationUsers
      .filter((user) => user.label.toLowerCase().includes(searchTerm))
      .sort()
      .slice(0, 5);
    setUsers(filteredUsers);
  }, [state, organisationUsers]);

  const enabled = !!state;

  if (!users.length) {
    return null;
  }

  return (
    <FloatingWrapper positioner="cursor" enabled={enabled} placement="bottom-start">
      <div
        {...getMenuProps()}
        className="bg-white dark:bg-[#242424] p-2 rounded-md w-48 border border-gray-300 shadow-sm"
      >
        {enabled &&
          users.map((user, index) => {
            const isHighlighted = indexIsSelected(index);

            return (
              <div
                key={user.id}
                {...getItemProps({
                  item: user,
                  index,
                })}
              >
                <SelectItemContent icon={UserIcon} active={isHighlighted} idle={false} disabled={false}>
                  {user.label}
                </SelectItemContent>
              </div>
            );
          })}
      </div>
    </FloatingWrapper>
  );
};

export const MentionSuggestions = memo(MentionSuggestions_);
